import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Calendar, 
  Users, 
  MapPin, 
  Clock, 
  BookOpen, 
  TrendingUp,
  Plus,
  Settings,
  Download,
  Upload,
  CheckCircle,
  AlertCircle,
  UserCheck,
  Presentation,
  Building,
  Coffee,
  Award,
  MessageSquare,
  Activity,
  ArrowLeft,
  BarChart3
} from "lucide-react";
import { supabase } from '@/integrations/supabase/client';
import { toast } from "sonner";

interface LearningEvent {
  id: string;
  title: string;
  date: string;
  status: 'planning' | 'registration_open' | 'in_progress' | 'completed';
  totalSessions: number;
  totalAttendees: number;
  venue: string;
  registrationDeadline: string;
}

interface ConferenceSession {
  id: string;
  eventId: string;
  title: string;
  presenter: string;
  room: string;
  startTime: string;
  endTime: string;
  capacity: number;
  enrolled: number;
  waitlist: number;
  category: string;
  description: string;
  materials: string[];
}

export default function QuarterlyLearningDays() {
  const navigate = useNavigate();
  const [events, setEvents] = useState<LearningEvent[]>([]);
  const [sessions, setSessions] = useState<ConferenceSession[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<string>('');
  const [loading, setLoading] = useState(true);

  // Mock data for demonstration
  useEffect(() => {
    loadMockData();
  }, []);

  const loadMockData = () => {
    const mockEvents: LearningEvent[] = [
      {
        id: '1',
        title: 'Fall 2024 Professional Development Conference',
        date: '2024-10-15',
        status: 'registration_open',
        totalSessions: 24,
        totalAttendees: 245,
        venue: 'District Conference Center',
        registrationDeadline: '2024-10-01'
      },
      {
        id: '2', 
        title: 'Winter 2025 Learning Summit',
        date: '2025-01-20',
        status: 'planning',
        totalSessions: 0,
        totalAttendees: 0,
        venue: 'Main Campus Auditorium',
        registrationDeadline: '2025-01-05'
      },
      {
        id: '3',
        title: 'Summer 2024 Innovation Days',
        date: '2024-07-12',
        status: 'completed',
        totalSessions: 18,
        totalAttendees: 189,
        venue: 'Technology Center',
        registrationDeadline: '2024-06-28'
      }
    ];

    const mockSessions: ConferenceSession[] = [
      {
        id: '1',
        eventId: '1',
        title: 'AI Integration in the Classroom',
        presenter: 'Dr. Sarah Johnson',
        room: 'Room A',
        startTime: '09:00',
        endTime: '10:30',
        capacity: 30,
        enrolled: 28,
        waitlist: 5,
        category: 'Technology',
        description: 'Explore practical applications of AI tools in educational settings',
        materials: ['Handout PDF', 'Resource Links', 'Sample Activities']
      },
      {
        id: '2',
        eventId: '1',
        title: 'Inclusive Teaching Strategies',
        presenter: 'Maria Rodriguez',
        room: 'Room B',
        startTime: '09:00',
        endTime: '10:30',
        capacity: 25,
        enrolled: 22,
        waitlist: 0,
        category: 'Pedagogy',
        description: 'Best practices for creating inclusive learning environments',
        materials: ['Strategy Guide', 'Assessment Rubric']
      },
      {
        id: '3',
        eventId: '1',
        title: 'Data-Driven Decision Making',
        presenter: 'John Smith',
        room: 'Room C',
        startTime: '11:00',
        endTime: '12:30',
        capacity: 40,
        enrolled: 35,
        waitlist: 2,
        category: 'Analytics',
        description: 'Using student data to inform instructional decisions',
        materials: ['Data Templates', 'Analysis Tools']
      }
    ];

    setEvents(mockEvents);
    setSessions(mockSessions);
    setSelectedEvent('1');
    setLoading(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'planning': return 'secondary';
      case 'registration_open': return 'default';
      case 'in_progress': return 'destructive';
      case 'completed': return 'outline';
      default: return 'secondary';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'planning': return 'Planning';
      case 'registration_open': return 'Registration Open';
      case 'in_progress': return 'In Progress';
      case 'completed': return 'Completed';
      default: return status;
    }
  };

  const selectedEventData = events.find(e => e.id === selectedEvent);
  const eventSessions = sessions.filter(s => s.eventId === selectedEvent);

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-48 bg-muted rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate('/admin')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Admin
          </Button>
          <div className="space-y-1">
            <h1 className="text-3xl font-bold text-foreground">Quarterly Learning Days</h1>
            <p className="text-muted-foreground">Manage conference-style professional development events</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/admin/reporting')}>
            <BarChart3 className="h-4 w-4 mr-2" />
            Generate Reports
          </Button>
          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Create Event
          </Button>
        </div>
      </div>

      {/* Event Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {events.map((event) => (
          <Card 
            key={event.id} 
            className={`cursor-pointer transition-all hover:shadow-lg ${
              selectedEvent === event.id ? 'ring-2 ring-primary' : ''
            }`}
            onClick={() => setSelectedEvent(event.id)}
          >
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <CardTitle className="text-lg leading-tight">{event.title}</CardTitle>
                  <CardDescription className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {new Date(event.date).toLocaleDateString()}
                  </CardDescription>
                </div>
                <Badge variant={getStatusColor(event.status) as any}>
                  {getStatusLabel(event.status)}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-muted-foreground" />
                  <span>{event.totalSessions} Sessions</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span>{event.totalAttendees} Attendees</span>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4" />
                <span>{event.venue}</span>
              </div>
              {event.status === 'registration_open' && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Registration closes: {new Date(event.registrationDeadline).toLocaleDateString()}
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Detailed Event Management */}
      {selectedEventData && (
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="sessions">Sessions</TabsTrigger>
            <TabsTrigger value="attendees">Attendees</TabsTrigger>
            <TabsTrigger value="venues">Venues</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
            <TabsTrigger value="materials">Materials</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <BookOpen className="h-4 w-4" />
                    Total Sessions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{selectedEventData.totalSessions}</div>
                  <p className="text-sm text-muted-foreground">Across 8 time slots</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Registered Users
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{selectedEventData.totalAttendees}</div>
                  <p className="text-sm text-muted-foreground">89% capacity</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Building className="h-4 w-4" />
                    Venues
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">12</div>
                  <p className="text-sm text-muted-foreground">Rooms & spaces</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Award className="h-4 w-4" />
                    PD Hours
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">6</div>
                  <p className="text-sm text-muted-foreground">Credits earned</p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Event Timeline</CardTitle>
                <CardDescription>Key milestones and deadlines</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { date: '2024-09-01', title: 'Session Proposals Due', status: 'completed' },
                    { date: '2024-09-15', title: 'Schedule Finalized', status: 'completed' },
                    { date: '2024-10-01', title: 'Registration Closes', status: 'upcoming' },
                    { date: '2024-10-10', title: 'Materials Distributed', status: 'upcoming' },
                    { date: '2024-10-15', title: 'Event Day', status: 'upcoming' }
                  ].map((milestone, index) => (
                    <div key={index} className="flex items-center gap-4">
                      <div className={`w-3 h-3 rounded-full ${
                        milestone.status === 'completed' ? 'bg-success' : 'bg-muted'
                      }`} />
                      <div className="flex-1">
                        <div className="font-medium">{milestone.title}</div>
                        <div className="text-sm text-muted-foreground">
                          {new Date(milestone.date).toLocaleDateString()}
                        </div>
                      </div>
                      <Badge variant={milestone.status === 'completed' ? 'secondary' : 'outline'}>
                        {milestone.status === 'completed' ? <CheckCircle className="h-3 w-3 mr-1" /> : <Clock className="h-3 w-3 mr-1" />}
                        {milestone.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="sessions" className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Session Management</h3>
                <p className="text-sm text-muted-foreground">Manage conference sessions and schedules</p>
              </div>
              <div className="flex gap-2">
                <Select defaultValue="all">
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Filter by category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="technology">Technology</SelectItem>
                    <SelectItem value="pedagogy">Pedagogy</SelectItem>
                    <SelectItem value="analytics">Analytics</SelectItem>
                  </SelectContent>
                </Select>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Session
                </Button>
              </div>
            </div>

            <div className="grid gap-4">
              {eventSessions.map((session) => (
                <Card key={session.id}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="space-y-2">
                        <CardTitle className="text-lg">{session.title}</CardTitle>
                        <CardDescription className="flex items-center gap-4">
                          <span className="flex items-center gap-1">
                            <UserCheck className="h-4 w-4" />
                            {session.presenter}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="h-4 w-4" />
                            {session.room}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {session.startTime} - {session.endTime}
                          </span>
                        </CardDescription>
                      </div>
                      <Badge variant="outline">{session.category}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">{session.description}</p>
                      
                      <div className="grid grid-cols-3 gap-4">
                        <div className="text-center p-3 bg-muted/50 rounded">
                          <div className="text-lg font-semibold">{session.capacity}</div>
                          <div className="text-xs text-muted-foreground">Capacity</div>
                        </div>
                        <div className="text-center p-3 bg-primary/5 rounded">
                          <div className="text-lg font-semibold text-primary">{session.enrolled}</div>
                          <div className="text-xs text-muted-foreground">Enrolled</div>
                        </div>
                        <div className="text-center p-3 bg-warning/5 rounded">
                          <div className="text-lg font-semibold text-warning">{session.waitlist}</div>
                          <div className="text-xs text-muted-foreground">Waitlist</div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex gap-2">
                          {session.materials.map((material, index) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {material}
                            </Badge>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline">
                            <Settings className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                          <Button size="sm">
                            <Users className="h-4 w-4 mr-1" />
                            Manage
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="attendees" className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Attendee Management</h3>
                <p className="text-sm text-muted-foreground">Track registrations and manage attendee data</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline">
                  <Upload className="h-4 w-4 mr-2" />
                  Import List
                </Button>
                <Button variant="outline">
                  <Download className="h-4 w-4 mr-2" />
                  Export List
                </Button>
                <Button>
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Send Update
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Total Registered</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">245</div>
                  <p className="text-sm text-success">+12 this week</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Pending</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">18</div>
                  <p className="text-sm text-muted-foreground">Awaiting approval</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Waitlisted</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">7</div>
                  <p className="text-sm text-muted-foreground">Popular sessions</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Check-ins</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">0</div>
                  <p className="text-sm text-muted-foreground">Event day only</p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Registration by Department</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { dept: 'Elementary', count: 89, percentage: 36 },
                    { dept: 'Middle School', count: 67, percentage: 27 },
                    { dept: 'High School', count: 54, percentage: 22 },
                    { dept: 'Special Education', count: 23, percentage: 9 },
                    { dept: 'Administration', count: 12, percentage: 5 }
                  ].map((dept, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full bg-primary" />
                        <span className="font-medium">{dept.dept}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="w-24 bg-muted rounded-full h-2">
                          <div 
                            className="bg-primary h-2 rounded-full" 
                            style={{ width: `${dept.percentage}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium">{dept.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="venues" className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Venue Management</h3>
                <p className="text-sm text-muted-foreground">Manage rooms, equipment, and logistics</p>
              </div>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Venue
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { name: 'Room A', capacity: 30, sessions: 4, equipment: ['Projector', 'Whiteboard', 'Sound System'] },
                { name: 'Room B', capacity: 25, sessions: 3, equipment: ['Projector', 'Whiteboard'] },
                { name: 'Auditorium', capacity: 150, sessions: 2, equipment: ['AV System', 'Stage', 'Microphones'] },
                { name: 'Computer Lab', capacity: 20, sessions: 3, equipment: ['Computers', 'Projector', 'Software'] },
                { name: 'Cafeteria', capacity: 200, sessions: 1, equipment: ['Tables', 'Chairs', 'Serving Area'] },
                { name: 'Library', capacity: 40, sessions: 2, equipment: ['Tables', 'Books', 'Quiet Space'] }
              ].map((venue, index) => (
                <Card key={index}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Building className="h-4 w-4" />
                      {venue.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Capacity</span>
                        <div className="font-semibold">{venue.capacity} people</div>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Sessions</span>
                        <div className="font-semibold">{venue.sessions} scheduled</div>
                      </div>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">Equipment</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {venue.equipment.map((item, i) => (
                          <Badge key={i} variant="outline" className="text-xs">
                            {item}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <Button size="sm" variant="outline" className="w-full">
                      <Settings className="h-4 w-4 mr-2" />
                      Manage
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="analytics" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    Attendance Rate
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">94%</div>
                  <p className="text-sm text-success">+5% vs last quarter</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Award className="h-4 w-4" />
                    Completion Rate
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">87%</div>
                  <p className="text-sm text-success">Above target</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Presentation className="h-4 w-4" />
                    Avg. Rating
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">4.6</div>
                  <p className="text-sm text-muted-foreground">Out of 5 stars</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Activity className="h-4 w-4" />
                    Engagement
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">92%</div>
                  <p className="text-sm text-success">High interaction</p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Popular Session Categories</CardTitle>
                <CardDescription>Based on enrollment and feedback</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { category: 'Technology Integration', sessions: 8, avgRating: 4.7, enrollment: 185 },
                    { category: 'Classroom Management', sessions: 6, avgRating: 4.5, enrollment: 142 },
                    { category: 'Assessment Strategies', sessions: 4, avgRating: 4.3, enrollment: 98 },
                    { category: 'Special Education', sessions: 3, avgRating: 4.8, enrollment: 67 },
                    { category: 'Leadership', sessions: 3, avgRating: 4.4, enrollment: 45 }
                  ].map((cat, index) => (
                    <div key={index} className="flex items-center justify-between p-3 border rounded">
                      <div>
                        <div className="font-medium">{cat.category}</div>
                        <div className="text-sm text-muted-foreground">
                          {cat.sessions} sessions • {cat.enrollment} total enrollments
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold">{cat.avgRating}</div>
                        <div className="text-sm text-muted-foreground">Avg. Rating</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="materials" className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Materials & Resources</h3>
                <p className="text-sm text-muted-foreground">Manage session materials and resources</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline">
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Materials
                </Button>
                <Button>
                  <Coffee className="h-4 w-4 mr-2" />
                  Catering Menu
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Pre-Event Materials</CardTitle>
                  <CardDescription>Resources distributed before the event</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {[
                    'Event Schedule & Map',
                    'Presenter Biographies',
                    'Session Handouts',
                    'Parking Instructions',
                    'Welcome Packet'
                  ].map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-2 border rounded">
                      <span className="text-sm">{item}</span>
                      <Button size="sm" variant="outline">
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Logistics Checklist</CardTitle>
                  <CardDescription>Event day preparation items</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {[
                    { item: 'Room Setup Complete', status: 'pending' },
                    { item: 'AV Equipment Tested', status: 'completed' },
                    { item: 'Catering Confirmed', status: 'completed' },
                    { item: 'Registration Desk Ready', status: 'pending' },
                    { item: 'Materials Distributed', status: 'pending' }
                  ].map((task, index) => (
                    <div key={index} className="flex items-center justify-between p-2 border rounded">
                      <span className="text-sm">{task.item}</span>
                      <Badge variant={task.status === 'completed' ? 'secondary' : 'outline'}>
                        {task.status === 'completed' ? <CheckCircle className="h-3 w-3 mr-1" /> : <Clock className="h-3 w-3 mr-1" />}
                        {task.status}
                      </Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Catering & Hospitality</CardTitle>
                <CardDescription>Meal planning and special accommodations</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-3">
                    <h4 className="font-semibold">Morning Coffee</h4>
                    <div className="text-sm text-muted-foreground space-y-1">
                      <div>• Regular & Decaf Coffee</div>
                      <div>• Tea Selection</div>
                      <div>• Pastries & Fruit</div>
                      <div>• Juice & Water</div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <h4 className="font-semibold">Lunch</h4>
                    <div className="text-sm text-muted-foreground space-y-1">
                      <div>• Sandwich Platters</div>
                      <div>• Salad Bar</div>
                      <div>• Vegetarian Options</div>
                      <div>• Desserts</div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <h4 className="font-semibold">Special Diets</h4>
                    <div className="text-sm text-muted-foreground space-y-1">
                      <div>• Gluten-Free: 12 requests</div>
                      <div>• Vegetarian: 23 requests</div>
                      <div>• Vegan: 8 requests</div>
                      <div>• Allergies: 5 requests</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}